import { useEffect, useState } from 'react';

import { clearInterval, setInterval } from 'worker-timers';

/**
 * Counts down to zero and stops there.
 *
 * The starting time is read once, when the clock is first mounted — a later
 * change to the arguments does not restart it. Give the component a `key` to
 * start a new count.
 */
export function useCountDownTimer(
  hours: number,
  minutes: number,
  seconds: number,
) {
  const [[hrs, mins, secs], setTime] = useState([hours, minutes, seconds]);

  const finished = hrs === 0 && mins === 0 && secs === 0;

  useEffect(() => {
    // Nothing left to count. Writing [0, 0, 0] over [0, 0, 0] would be a new
    // array every second — a render a second, for as long as the clock is on
    // screen, over a value that cannot change again.
    if (finished) {
      return;
    }

    // Stepped from the state React holds rather than from what this render
    // closed over, so the interval outlives the tick it was started on and is
    // laid down once per count instead of once per second.
    const timerId = setInterval(() => {
      setTime(([h, m, s]) => {
        if (s > 0) {
          return [h, m, s - 1];
        }

        if (m > 0) {
          return [h, m - 1, 59];
        }

        return [h - 1, 59, 59];
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [finished]);

  return [hrs, mins, secs] as const;
}

/**
 * The seconds-only count, which is every clock in the app so far — saves each
 * caller spelling out the two units it does not use and naming what it gets out
 * of a positional read.
 */
export function useCountDownSeconds(seconds: number) {
  const [, , secondsLeft] = useCountDownTimer(0, 0, seconds);

  return secondsLeft;
}
